import React from 'react';
import Nav from './Nav';
import DotCalendarCluster from './DotCalendarCluster';
import HeroMoodBoardMock, { MOOD_BOARD_SEQUENCE_DURATION } from './HeroMoodBoardMock';
import { useIsMobile } from '../../hooks/useIsMobile';
import { SHELL_WHITE, alpha } from '../../theme/colors';
import Button from '../shared/Button';

const EYEBROW = 'The GLP-1 tracker built for female biology';
const HEADLINE = "Your health is not a score.\nIt's a pattern — and patterns can be read.";
const BODY =
  'Vesna brings together your GLP-1, lifestyle protocols, hormones, and daily habits so the patterns in your body finally make sense.';

const CTAButton = ({ onClick, style }) => (
  <Button
    tone="gradient"
    onClick={onClick}
    style={{
      width: 'fit-content',
      borderRadius: 999,
      fontFamily: 'DM Sans',
      fontSize: 15,
      ...style,
    }}
  >
    Get started
  </Button>
);

const ScrollCue = () => (
  <div style={{ position: 'relative', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'heroScrollBounce 2s infinite' }}>
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 7L10 13L16 7" stroke={alpha('white', 0.6)} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <style>{`@keyframes heroScrollBounce { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }`}</style>
  </div>
);

// Full-bleed photo background behind everything, rather than a contained
// image box — text and the two mockup overlays (DotCalendar row, Mood
// Board) float directly on top of the photo. Overlay left/top values are
// lifted straight from the approved Paper frames (3WQ-0 desktop, 4NA-0
// mobile), positioned relative to the same 64px/20px-padded content column
// used everywhere else on the page, not the raw viewport.
const Hero = ({ onSignIn, onGetStarted }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/landing-assets/hero.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: '50%',
          }}
        />
        <div style={{ position: 'relative', padding: '20px 20px 0' }}>
          <Nav onSignIn={onSignIn} onGetStarted={onGetStarted} />
        </div>

        <div style={{ position: 'relative', width: '100%', height: 607.5 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', padding: '32px 20px 36px' }}>
            <span
              style={{
                fontFamily: 'DM Sans',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: SHELL_WHITE,
                textShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            >
              {EYEBROW}
            </span>
            <h1
              style={{
                fontFamily: 'Instrument Serif',
                fontSize: 32,
                fontWeight: 400,
                lineHeight: '118%',
                color: SHELL_WHITE,
                margin: 0,
                marginTop: 14,
                whiteSpace: 'pre-line',
                textShadow: '0 2px 8px rgba(0,0,0,0.4)',
              }}
            >
              {HEADLINE}
            </h1>
            <span
              style={{
                fontFamily: 'DM Sans',
                fontSize: 14,
                lineHeight: '160%',
                color: SHELL_WHITE,
                opacity: 0.85,
                marginTop: 14,
                textShadow: '0 1px 7px rgba(0,0,0,0.9)',
              }}
            >
              {BODY}
            </span>
            <CTAButton onClick={onGetStarted} style={{ padding: '13px 26px', marginTop: 22 }} />
          </div>

          <div style={{ position: 'absolute', top: 363.5, right: 40, display: 'flex', justifyContent: 'flex-end' }}>
            <DotCalendarCluster variant="mobile" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: 900, overflow: 'hidden' }}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(/landing-assets/hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: '50%',
        }}
      />

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', padding: '40px 64px' }}>
        <Nav onSignIn={onSignIn} onGetStarted={onGetStarted} />

        <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', paddingTop: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: 480, flexShrink: 0 }}>
            <span
              style={{
                fontFamily: 'DM Sans',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: SHELL_WHITE,
                textShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            >
              {EYEBROW}
            </span>
            <h1
              style={{
                fontFamily: 'Instrument Serif',
                fontSize: 52,
                fontWeight: 400,
                lineHeight: '115%',
                color: SHELL_WHITE,
                margin: 0,
                marginTop: 20,
                whiteSpace: 'pre-line',
                textShadow: '0 2px 8px rgba(0,0,0,0.4)',
              }}
            >
              {HEADLINE}
            </h1>
            <div
              style={{
                fontFamily: 'DM Sans',
                fontSize: 16,
                lineHeight: 1.75,
                color: SHELL_WHITE,
                marginTop: 24,
                width: 420,
                textShadow: '0 1px 4px rgba(0,0,0,0.3)',
              }}
            >
              {BODY}
            </div>
            <CTAButton onClick={onGetStarted} style={{ padding: '14px 28px', marginTop: 36 }} />
          </div>

          {/* Anchored to the right edge (not a fixed left) so both stay
              correctly placed as the Hero Content container's own width
              changes between the 1024px breakpoint and its 1312px max —
              a fixed left, lifted straight from the 1312px-wide Paper frame,
              would drift/overflow at narrower desktop widths. Right offsets
              are derived from each element's width against that 1312px
              frame: cluster (4*190 + 3*16 = 808 wide) at left 509 sits
              flush with the right edge; mood board (380 wide) at left 860
              sits 72px in from it. */}
          <DotCalendarCluster layout="row" startDelay={MOOD_BOARD_SEQUENCE_DURATION} style={{ position: 'absolute', right: 0, top: 566 }} />
          <HeroMoodBoardMock style={{ position: 'absolute', right: 72, top: 31, width: 380 }} />
        </div>

        <ScrollCue />
      </div>
    </div>
  );
};

export default Hero;
